import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { menuItems } from "./Menu";
import { holidayLabel } from "../../../Constant/HR_HolidayTemplate/holidayTemplate";
import BaseModal from "../../../BaseComponents/BaseModal";
import { useFormik } from "formik";
import * as Yup from "yup";
import { StatusCodes } from "http-status-codes";
import { toast } from "react-toastify";
import {
  AttMode,
  MonthCalculation,
  MonthDuration,
  radioOptions,
} from "../../../Constant/HR/Attendence_Mode";
import {
  attendanceDataApi,
  AttendenceMode,
  CalculationMethod,
  salaryDurationApi,
} from "../../../Api/AttendenceMode";
import { checkStatusCodeSuccess, getLocalItem, settingError } from "../../../Constant/common";
import { useSelectedCenter } from "../../CenterContext";
import { validationMessages } from "../../../Constant/validation";
import { usePageLoader } from "../../../_helper/loading/LoadingContext";
import { ChevronRight, Settings } from "react-feather";
import { viewOrganizationApi } from "../../../Api/OrganizationApi";
import { listOfRolePermissionApi } from "../../../Api/roleApi";

/* ── Group menu items by category ── */
const groupByCategory = (items) =>
  items.reduce((acc, item) => {
    const cat = item.category || "Other";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

const CATEGORY_CONFIG = {
  Attendance: { color: "#6366f1", bg: "rgba(99,102,241,0.08)", border: "rgba(99,102,241,0.15)" },
  Payroll: { color: "#10b981", bg: "rgba(16,185,129,0.08)", border: "rgba(16,185,129,0.15)" },
  Reports: { color: "#f59e0b", bg: "rgba(245,158,11,0.08)", border: "rgba(245,158,11,0.15)" },
  Other: { color: "#64748b", bg: "rgba(100,116,139,0.08)", border: "rgba(100,116,139,0.15)" },
};

/* ── Setting card ── */
const SettingCard = ({ item, onClick }) => {
  const Icon = item.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "16px 18px",
        borderRadius: 14,
        width: "100%",
        textAlign: "left",
        background: "var(--glass-bg)",
        border: "1px solid var(--glass-border)",
        cursor: "pointer",
        transition: "all 0.18s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = `${item.iconBg}`;
        e.currentTarget.style.borderColor = `${item.iconColor}40`;
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.boxShadow = `0 8px 24px ${item.iconColor}18`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "var(--glass-bg)";
        e.currentTarget.style.borderColor = "var(--glass-border)";
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {/* Icon box */}
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 12,
          flexShrink: 0,
          background: item.iconBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon size={20} strokeWidth={1.75} style={{ color: item.iconColor }} />
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight: 700,
            fontSize: "0.9375rem",
            color: "var(--text-primary)",
            marginBottom: 3,
          }}
        >
          {item.label}
        </div>
        <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", lineHeight: 1.4 }}>
          {item.description}
        </div>
      </div>

      {/* Arrow */}
      <ChevronRight
        size={16}
        strokeWidth={2}
        style={{ color: "var(--text-muted)", flexShrink: 0 }}
      />
    </button>
  );
};

const Setting = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [attendenceMode, setAttendenceMode] = useState();
  const [monthDuration, setMonthDuration] = useState();
  const [btnLoader, setBtnLoader] = useState(false);
  const userPermission = JSON.parse(getLocalItem("permissions"));
  const { selectedCenterPayload } = useSelectedCenter();
  const { setLoading } = usePageLoader();
  const navigate = useNavigate();

  /* ── Formik: Attendance Mode ── */
  const formikAttendenceMode = useFormik({
    enableReinitialize: true,
    initialValues: { AttendenceMode: attendenceMode },
    validationSchema: Yup.object({
      AttendenceMode: Yup.string().required(validationMessages.required(AttMode.ATTENDENCE_MODE)),
    }),
    onSubmit: (values) => {
      setBtnLoader(true);
      AttendenceMode({ attendance_mode: values.AttendenceMode })
        .then((resp) => {
          if (
            [StatusCodes.ACCEPTED, StatusCodes.OK, StatusCodes.CREATED].includes(resp?.statusCode)
          ) {
            toast.success(resp?.message);
            fetchAttendance();
          } else toast.error(resp?.message);
        })
        .catch((err) => err)
        .finally(() => {
          setBtnLoader(false);
          formikAttendenceMode.resetForm();
          setAttendenceMode(null);
          setModalOpen(false);
        });
    },
  });

  const formikAttendenceModeRef = useRef(formikAttendenceMode);
  useEffect(() => {
    formikAttendenceModeRef.current = formikAttendenceMode;
  }, [formikAttendenceMode]);

  /* ── Formik: Month Duration ── */
  const formikMonthDuration = useFormik({
    enableReinitialize: true,
    initialValues: { MonthDuration: monthDuration },
    validationSchema: Yup.object({
      MonthDuration: Yup.string().required(
        validationMessages.required(MonthDuration.MONTH_CALCULATION),
      ),
    }),
    onSubmit: (values) => {
      setBtnLoader(true);
      CalculationMethod({ salary_calculation_method: values.MonthDuration })
        .then((resp) => {
          if (
            [StatusCodes.ACCEPTED, StatusCodes.OK, StatusCodes.CREATED].includes(resp?.statusCode)
          ) {
            toast.success(resp?.message);
            fetchSalary();
          } else toast.error(resp?.message);
        })
        .catch((err) => toast.error(err?.response?.data?.message || err?.message))
        .finally(() => {
          setBtnLoader(false);
          formikMonthDuration.resetForm();
          setMonthDuration(null);
          setModalOpen(false);
        });
    },
  });

  const formikMonthDurationRef = useRef(formikMonthDuration);
  useEffect(() => {
    formikMonthDurationRef.current = formikMonthDuration;
  }, [formikMonthDuration]);

  /* ── Fetch current values ── */
  const fetchSalary = useCallback(() => {
    setLoading(true);
    salaryDurationApi()
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setMonthDuration(resp?.data?.value);
          formikMonthDurationRef.current?.setFieldValue("MonthDuration", resp?.data?.value);
        } else toast.error(resp?.message);
      })
      .catch((err) => err)
      .finally(() => setLoading(false));
  }, [setLoading]);

  const fetchAttendance = useCallback(() => {
    setLoading(true);
    attendanceDataApi()
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setAttendenceMode(resp?.data?.value);
          formikAttendenceModeRef.current?.setFieldValue("AttendenceMode", resp?.data?.value);
        } else toast.error(resp?.message);
      })
      .catch((err) => err)
      .finally(() => setLoading(false));
  }, [setLoading]);

  const [isPayroll, setIsPayroll] = useState(true);
  const [hasOvertimePolicy, setHasOvertimePolicy] = useState(true);
  const [masterModulesLoaded, setMasterModulesLoaded] = useState(false);
  const [masterModules, setMasterModules] = useState([]);

  useEffect(() => {
    fetchSalary();
    fetchAttendance();

    const orgId = localStorage.getItem("organizationId");
    if (orgId) {
      // Fetch organization details
      viewOrganizationApi(orgId)
        .then((resp) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            const org = resp?.data;
            if (org?.is_payroll !== undefined && org?.is_payroll !== null) {
              setIsPayroll(org.is_payroll);
            }
            if (!org?.overtime_policy_type) {
              setHasOvertimePolicy(false);
            }
          }
        })
        .catch(() => {});

      // Fetch master modules
      listOfRolePermissionApi({ organization_id: Number(orgId) })
        .then((resp) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            const list = resp?.data?.listOfRolePermission || [];
            setMasterModules(list);
            setMasterModulesLoaded(true);
          }
        })
        .catch(() => {});
    }
  }, [fetchSalary, fetchAttendance]);

  useEffect(() => {
    if (selectedCenterPayload.center_id === undefined) {
      navigate("/globalConfiguration", { state: { fromSetting: true } });
      toast.warning(settingError);
    }
  }, [navigate, selectedCenterPayload.center_id]);

  /* ── Permission filter ── */
  const role = getLocalItem("role");
  const settingPermissions = userPermission?.filter(
    (p) => p.parent_module_name?.trim()?.toLowerCase() === "setting",
  );

  const isModuleInMaster = (label) => {
    if (!masterModulesLoaded) return true;
    return masterModules.some((m) => {
      const name = m.module_name?.trim()?.toLowerCase();
      const searchLabel = label?.trim()?.toLowerCase();
      if (searchLabel === "payroll template" && name === "payroll") {
        return true;
      }
      return name === searchLabel;
    });
  };

  const filteredMenuItems = menuItems.filter((item) => {
    const hasPermission = settingPermissions?.some(
      (p) => p.module_name?.trim()?.toLowerCase() === item.label?.trim()?.toLowerCase(),
    );

    const hasPayrollPermission = settingPermissions?.some(
      (p) => p.module_name?.trim()?.toLowerCase() === "payroll",
    );

    // Payroll Template is controlled by Payroll permission
    if (item.label === "Payroll Template") {
      return role !== "SuperAdmin" && hasPayrollPermission;
    }

    const hasBasePermission =
      role === "SuperAdmin" || role === "Admin" || item.alwaysVisible || hasPermission;

    if (!hasBasePermission) return false;

    // Hide payroll cards if payroll is not enabled
    if (item.category === "Payroll" && !isPayroll) {
      return false;
    }

    // Hide overtime if not enabled
    if (item.label === "Overtime" && !hasOvertimePolicy) {
      return false;
    }

    // Hide if not in master modules
    if (!isModuleInMaster(item.label)) {
      return false;
    }

    return true;
  });
  const grouped = groupByCategory(filteredMenuItems);

  /* ── Card click handler ── */
  const handleCardClick = (item) => {
    if (item.openModal) {
      setSelectedItem(item);
      setModalOpen(true);
    } else if (item.link) {
      navigate(item.link);
    }
  };

  /* ── Current value badge ── */
  const currentAttendanceLabel = radioOptions.find((o) => o.value === attendenceMode)?.label;
  const currentMonthLabel = MonthCalculation.find((o) => o.value === monthDuration)?.label;

  return (
    <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* ── Page header ── */}
      <div
        className="animate-slide-down"
        style={{ display: "flex", alignItems: "center", gap: 14 }}
      >
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 14,
            background: "rgba(99,102,241,0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Settings size={20} strokeWidth={1.75} style={{ color: "var(--brand-indigo)" }} />
        </div>
        <div>
          <h1
            style={{
              fontSize: "1.375rem",
              fontWeight: 800,
              margin: 0,
              color: "var(--text-primary)",
            }}
          >
            {holidayLabel.businessSetting}
          </h1>
          <p style={{ margin: 0, fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}>
            Configure attendance, payroll and reporting preferences
          </p>
        </div>
      </div>

      {/* ── Current config summary ── */}
      {(currentAttendanceLabel || currentMonthLabel) && (
        <div
          className="animate-slide-up"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
            padding: "14px 18px",
            borderRadius: 14,
            background: "linear-gradient(135deg,rgba(99,102,241,0.06),rgba(139,92,246,0.04))",
            border: "1px solid rgba(99,102,241,0.12)",
          }}
        >
          <span
            style={{
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: "var(--text-muted)",
              alignSelf: "center",
            }}
          >
            Current config:
          </span>
          {currentAttendanceLabel && (
            <span
              style={{
                padding: "4px 12px",
                borderRadius: 999,
                fontSize: "0.75rem",
                fontWeight: 700,
                background: "rgba(99,102,241,0.1)",
                color: "var(--brand-indigo)",
                border: "1px solid rgba(99,102,241,0.2)",
              }}
            >
              {AttMode.ATTENDENCEMODE}: {currentAttendanceLabel}
            </span>
          )}
          {currentMonthLabel && (
            <span
              style={{
                padding: "4px 12px",
                borderRadius: 999,
                fontSize: "0.75rem",
                fontWeight: 700,
                background: "rgba(6,182,212,0.1)",
                color: "#06b6d4",
                border: "1px solid rgba(6,182,212,0.2)",
              }}
            >
              {MonthDuration.MONTH_DURATION}: {currentMonthLabel}
            </span>
          )}
        </div>
      )}

      {/* ── Category sections ── */}
      {Object.entries(grouped).map(([category, items]) => {
        const cfg = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.Other;
        return (
          <div key={category} className="animate-slide-up">
            {/* Category label */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <div
                style={{
                  height: 20,
                  width: 3,
                  borderRadius: 999,
                  background: cfg.color,
                }}
              />
              <span
                style={{
                  fontSize: "0.6875rem",
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  color: cfg.color,
                }}
              >
                {category}
              </span>
              <div style={{ flex: 1, height: 1, background: "var(--glass-border)" }} />
            </div>

            {/* Cards grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: 10,
              }}
            >
              {items.map((item, i) => (
                <SettingCard key={i} item={item} onClick={() => handleCardClick(item)} />
              ))}
            </div>
          </div>
        );
      })}

      {/* ── Settings modal ── */}
      {selectedItem?.openModal && (
        <BaseModal
          isOpen={modalOpen}
          title={
            selectedItem.label === AttMode.ATTENDENCEMODE
              ? AttMode.MANUAL_ATTENDENCE
              : MonthDuration.MONTH_CALCULATION
          }
          toggler={() => setModalOpen(false)}
          submit={
            selectedItem.label === AttMode.ATTENDENCEMODE
              ? formikAttendenceMode.handleSubmit
              : formikMonthDuration.handleSubmit
          }
          submitText={AttMode.SUBMIT}
          loader={btnLoader}
          disabled={btnLoader}
        >
          {selectedItem.label === AttMode.ATTENDENCEMODE && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {radioOptions.map((opt) => {
                const active = attendenceMode === opt.value;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => {
                      setAttendenceMode(opt.value);
                      formikAttendenceMode.setFieldValue(AttMode.AttendenceMode, opt.value);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 12,
                      padding: "14px 16px",
                      borderRadius: 12,
                      textAlign: "left",
                      width: "100%",
                      border: `1.5px solid ${active ? "rgba(99,102,241,0.4)" : "var(--glass-border)"}`,
                      background: active ? "rgba(99,102,241,0.08)" : "var(--icon-btn-bg)",
                      cursor: "pointer",
                      transition: "all 0.15s",
                    }}
                  >
                    <div
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        flexShrink: 0,
                        marginTop: 2,
                        border: `2px solid ${active ? "var(--brand-indigo)" : "var(--glass-border)"}`,
                        background: active ? "var(--brand-indigo)" : "transparent",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {active && (
                        <div
                          style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff" }}
                        />
                      )}
                    </div>
                    <div>
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: "0.875rem",
                          color: active ? "var(--brand-indigo)" : "var(--text-primary)",
                        }}
                      >
                        {opt.label}
                      </div>
                      {opt.labelSuffix && (
                        <div
                          style={{
                            fontSize: "0.75rem",
                            color: "var(--text-muted)",
                            marginTop: 3,
                            lineHeight: 1.5,
                          }}
                        >
                          {opt.labelSuffix}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
              {formikAttendenceMode.touched.AttendenceMode &&
                formikAttendenceMode.errors.AttendenceMode && (
                  <div style={{ color: "#f43f5e", fontSize: "0.8125rem" }}>
                    {formikAttendenceMode.errors.AttendenceMode}
                  </div>
                )}
            </div>
          )}
          {selectedItem.label === MonthDuration.MONTH_DURATION && (
            <div>
              <p
                style={{
                  margin: "0 0 12px",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: "var(--text-muted)",
                }}
              >
                {MonthDuration.MONTHLYSALARY}
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {MonthCalculation.map((opt) => {
                  const active = monthDuration === opt.value;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => {
                        setMonthDuration(opt.value);
                        formikMonthDuration.setFieldValue(MonthDuration.MonthDuration, opt.value);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 12,
                        padding: "14px 16px",
                        borderRadius: 12,
                        textAlign: "left",
                        width: "100%",
                        border: `1.5px solid ${active ? "rgba(99,102,241,0.4)" : "var(--glass-border)"}`,
                        background: active ? "rgba(99,102,241,0.08)" : "var(--icon-btn-bg)",
                        cursor: "pointer",
                        transition: "all 0.15s",
                      }}
                    >
                      <div
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: "50%",
                          flexShrink: 0,
                          marginTop: 2,
                          border: `2px solid ${active ? "var(--brand-indigo)" : "var(--glass-border)"}`,
                          background: active ? "var(--brand-indigo)" : "transparent",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {active && (
                          <div
                            style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff" }}
                          />
                        )}
                      </div>
                      <div>
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: "0.875rem",
                            color: active ? "var(--brand-indigo)" : "var(--text-primary)",
                          }}
                        >
                          {opt.label}
                        </div>
                        {opt.labelSuffix && (
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: "var(--text-muted)",
                              marginTop: 3,
                              lineHeight: 1.5,
                            }}
                          >
                            {opt.labelSuffix}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
                {formikMonthDuration.touched.MonthDuration &&
                  formikMonthDuration.errors.MonthDuration && (
                    <div style={{ color: "#f43f5e", fontSize: "0.8125rem" }}>
                      {formikMonthDuration.errors.MonthDuration}
                    </div>
                  )}
              </div>
            </div>
          )}
        </BaseModal>
      )}
    </div>
  );
};

export default Setting;
